import { IPublicSession } from 'state/session';

/**
 * Tracks session keys whose local edit has not yet been confirmed by the server (AVS-4330).
 *
 * Two things consume this:
 *  - the remote-apply chokepoint, so an incoming payload never overwrites an edit that is
 *    still in flight (a read-repair legitimately carries a NEWER version, so the staleness
 *    gate alone cannot protect a pending local change);
 *  - the sync layer, which keeps keys dirty when a PUT fails so they can be re-sent instead
 *    of being silently lost.
 */

let dirtyKeys = new Set<keyof IPublicSession>();

export function markDirty(keys: Iterable<keyof IPublicSession>): void {
  for (const key of keys) {
    dirtyKeys.add(key);
  }
}

export function getDirtyKeys(): ReadonlySet<keyof IPublicSession> {
  return dirtyKeys;
}

/**
 * Clear only the keys a completed request actually carried (its snapshot). A key first dirtied
 * AFTER the snapshot is not in `keys`, so it survives and is re-sent by a later request. A key
 * that WAS in the snapshot but is re-edited mid-flight cannot be distinguished from its older
 * generation (this is a plain Set), so it IS cleared here — harmless under full-body PUTs (the
 * next PUT re-sends the whole session) but a lost-update risk if `deltaSessionSync` is enabled
 * (Gate I9). Distinguishing generations would require a Map<key, gen>, deferred with that gate.
 */
export function clearDirty(keys: Iterable<keyof IPublicSession>): void {
  for (const key of keys) {
    dirtyKeys.delete(key);
  }
}

export function resetDirtyKeys(): void {
  dirtyKeys = new Set();
}
